/** The congestion-pricing maps: the charging zone and the roads around it, coloured
 *  by V/C (a stored run) or by its change from the baseline.
 *    WorkshopMap  the full, pannable map on Origin-Destination Analytics
 *    ThumbMap     a small still of one run for the comparison row; no basemap,
 *                 so five of them don't each load Mapbox tiles
 *  Read-only: nothing here edits or runs anything. */

import { useEffect, useMemo, useRef } from 'react';
import { DeckGL } from '@deck.gl/react';
import { WebMercatorViewport } from '@deck.gl/core';
import { Map } from 'react-map-gl';
import { GeoJsonLayer, PathLayer, PolygonLayer } from '@deck.gl/layers';
import { losColor, vcDeltaColor, LOS_LEGEND, VC_DELTA_LEGEND } from '../../utils/networkEditor';
import { BAND_OUTLINE } from '../../utils/corridors';
import { zoneBox, zonePolygons } from './api';
import { useReleaseWebGL } from './webgl';

// White: no V/C grade or change class is white, so the zone never reads as a road.
const ZONE_FILL = [255, 255, 255, 24];
const ZONE_LINE = [255, 255, 255, 230];
// The main map's protected neighbourhoods.
const BAND_FILL = [...BAND_OUTLINE, 36];
const BAND_LINE = [...BAND_OUTLINE, 230];
const NO_BANDS = [null, null];
const BAND_SWATCH = { borderColor: `rgb(${BAND_OUTLINE.join(',')})`, backgroundColor: `rgba(${BAND_OUTLINE.join(',')}, 0.25)` };

const ROADS_VIEW = { longitude: -122.405, latitude: 37.786, zoom: 12.6, pitch: 0, bearing: 0 };
export const REGION_VIEW = { longitude: -122.2, latitude: 37.72, zoom: 9.2, pitch: 0, bearing: 0 };
const THUMB_ZOOM = 11.1;

function roadLayers({ id, zone, roads, roadMode, width = 2 }) {
  const out = [];
  if (roads?.features?.length) {
    const diff = roadMode === 'difference';
    out.push(new GeoJsonLayer({
      id: `${id}-roads-${roadMode}`,
      data: roads,
      stroked: true,
      filled: false,
      lineWidthUnits: 'pixels',
      getLineWidth: diff ? width + 1 : width,
      getLineColor: (f) => (diff
        ? vcDeltaColor(f.properties.deltaVc)
        : losColor(f.properties.vc, 200)),
      pickable: id === 'ws',
      updateTriggers: { getLineColor: [roadMode] },
    }));
  }
  const z = zoneLayer(id, zone, width + 2);
  if (z) out.push(z);
  return out;
}

/** The charging zone, outlined and lightly filled, or null without one. `part` 'fill' or
 *  'line' draws only that, for a map that puts other layers between the two. */
export function zoneLayer(id, zone, lineWidth, part = null) {
  const polygons = zonePolygons(zone?.geometry);
  if (!polygons.length) return null;
  return new PolygonLayer({
    id: part ? `${id}-zone-${part}` : `${id}-zone`,
    data: polygons.map((polygon) => ({ polygon })),
    getPolygon: (d) => d.polygon,
    stroked: part !== 'fill',
    filled: part !== 'line',
    getFillColor: ZONE_FILL,
    getLineColor: ZONE_LINE,
    lineWidthUnits: 'pixels',
    getLineWidth: lineWidth,
    pickable: false,
  });
}

/** The protected neighbourhoods as the main map draws them, [fill, outline], from
 *  `outlines` ([{ ring }], api.js getBandOutlines); nulls without any. The caller puts
 *  the two where its other layers allow. */
export function bandLayers(id, outlines) {
  if (!outlines || !outlines.length) return NO_BANDS;
  return [
    new PolygonLayer({
      id: `${id}-bands-fill`, data: outlines, getPolygon: (o) => o.ring,
      getFillColor: BAND_FILL, filled: true, stroked: false, pickable: false,
    }),
    new PathLayer({
      id: `${id}-bands-line`, data: outlines, getPath: (o) => o.ring,
      getColor: BAND_LINE, getWidth: 5, widthMinPixels: 3, pickable: false,
    }),
  ];
}

/** The legend's row for the protected neighbourhoods while they are drawn, or why they
 *  are not; nothing otherwise. `bands`: useBands' { outlines } or { error }. */
export function BandsLegendRow({ bands }) {
  if (bands && bands.outlines && bands.outlines.length) {
    return (
      <div className="ws-legend-row">
        <span className="ws-swatch ws-swatch-band" style={BAND_SWATCH} />Protected neighbourhoods
      </div>
    );
  }
  return bands && bands.error ? <div className="ws-legend-note" role="status">{bands.error}</div> : null;
}

const viewAt = (focus, zoom) => (focus
  ? { ...ROADS_VIEW, longitude: focus.lng, latitude: focus.lat, zoom }
  : { ...ROADS_VIEW, zoom });

/** With `onViewChange`, the map says what part of it is in view, { bounds: [w, s, e, n],
 *  zoom }: when it opens, then whenever it has stopped moving for VIEW_SETTLE_MS. */
const VIEW_SETTLE_MS = 350;

/** `bands`: the protected neighbourhoods to draw, useBands' { outlines } or { error }. */
export default function WorkshopMap({ zone, roads, roadMode, focus, legendNote, onViewChange, bands, children }) {
  const host = useRef(null);
  useReleaseWebGL(host);
  const initialViewState = useMemo(() => viewAt(focus, ROADS_VIEW.zoom), [focus]);
  const settle = useRef(null);
  const tellView = useRef(null);
  tellView.current = (viewState) => {
    const el = host.current;
    if (!onViewChange || !el || !el.clientWidth || !el.clientHeight) return;
    const [w, s, e, n] = new WebMercatorViewport({ ...viewState, width: el.clientWidth, height: el.clientHeight }).getBounds();
    onViewChange({ bounds: [w, s, e, n], zoom: viewState.zoom });
  };
  useEffect(() => {
    tellView.current(initialViewState);
    return () => clearTimeout(settle.current);
  }, [initialViewState]);
  const onViewStateChange = ({ viewState }) => {
    clearTimeout(settle.current);
    settle.current = setTimeout(() => tellView.current(viewState), VIEW_SETTLE_MS);
  };
  const outlines = bands ? bands.outlines : null;
  const bandParts = useMemo(() => bandLayers('ws', outlines), [outlines]);
  // As on the main map, the fill goes under the roads, so it tints the ground and not the
  // V/C colours; the outline goes over everything.
  const layers = useMemo(
    () => [bandParts[0], ...roadLayers({ id: 'ws', zone, roads, roadMode }), bandParts[1]].filter(Boolean),
    [roads, roadMode, zone, bandParts],
  );

  const getTooltip = ({ object, layer }) => {
    if (!object || !layer || !layer.id.startsWith('ws-roads')) return null;
    const p = object.properties;
    if (roadMode === 'difference') {
      const d = p.deltaVc;
      return `${p.name || 'Unnamed road'}\nV/C ${fmtVc(p.vcA)} → ${fmtVc(p.vcB)}`
        + (d == null ? '' : ` (${d > 0 ? '+' : ''}${d.toFixed(2)})`);
    }
    return `${p.name || 'Unnamed road'}\nV/C ${fmtVc(p.vc)} · LOS ${p.los}\n${Math.round(p.vehCount)} vehicles`;
  };

  return (
    <div className="ws-map" ref={host}>
      <DeckGL
        initialViewState={initialViewState}
        onViewStateChange={onViewChange ? onViewStateChange : undefined}
        controller={true}
        layers={layers}
        pickingRadius={6}
        getTooltip={getTooltip}
      >
        <Map
          mapStyle="mapbox://styles/mapbox/dark-v10"
          // Required by the 511 Data Agreement for the Muni schedules in Valhalla's tiles.
          customAttribution='<a href="https://511.org" target="_blank" rel="noopener noreferrer">Bay Area transit: data provided by 511.org</a>'
        />
      </DeckGL>
      {children}
      <RoadLegend mode={roadMode} note={legendNote} showZone={!!zone?.geometry} bands={bands} />
    </div>
  );
}

/** One run's roads (LOS colours) and the zone, fixed on the zone. */
export function ThumbMap({ zone, roads, focus }) {
  const host = useRef(null);
  useReleaseWebGL(host);
  const viewState = useMemo(() => {
    if (!zonePolygons(zone?.geometry).length) return viewAt(focus, THUMB_ZOOM);
    const [[w, s], , [e, n]] = zoneBox(zone.geometry, 0);
    return viewAt({ lng: (w + e) / 2, lat: (s + n) / 2 }, THUMB_ZOOM);
  }, [zone, focus]);
  const layers = useMemo(
    () => roadLayers({ id: 'ws-thumb', zone, roads, roadMode: 'run', width: 1 }),
    [roads, zone],
  );
  return (
    <div className="ws-thumb-map" ref={host}>
      <DeckGL viewState={viewState} controller={false} layers={layers} />
    </div>
  );
}

const fmtVc = (v) => (v == null ? '—' : Number(v).toFixed(2));

function RoadLegend({ mode, note, showZone, bands }) {
  const rows = mode === 'difference'
    ? VC_DELTA_LEGEND.map((b) => ({ label: b.label, color: b.color }))
    : LOS_LEGEND.map((s) => ({ label: `LOS ${s.grade} · V/C ${s.range}`, color: s.color }));
  return (
    <div className="ws-legend">
      <div className="ws-legend-title">
        {mode === 'difference' ? 'V/C change, baseline → scenario' : 'Road V/C (volume / capacity)'}
      </div>
      {rows.map((r) => (
        <div className="ws-legend-row" key={r.label}>
          <span className="ws-swatch" style={{ background: `rgb(${r.color.join(',')})` }} />{r.label}
        </div>
      ))}
      {showZone && (
        <div className="ws-legend-row">
          <span className="ws-swatch ws-swatch-zone" />Congestion pricing zone
        </div>
      )}
      <BandsLegendRow bands={bands} />
      {note && <div className="ws-legend-note">{note}</div>}
    </div>
  );
}
